import { forwardRef, useCallback, useMemo } from 'react'

import _ from 'lodash'
import mergeRefs from 'merge-refs'

import { useSearch } from './use-search'

import { IconButton } from '@/components/ui/icon-button'

import CloseIcon from '@/components/icons/close'
import SearchIcon from '@/components/icons/search'
import { FormInput, type FormInputProps } from '../ui/form-input'

type HeaderSearchInputProps = FormInputProps

const SearchInput = forwardRef<HTMLInputElement, HeaderSearchInputProps>(
  function SearchInput(
    { ...inputProps },
    internalRef: React.Ref<HTMLInputElement>
  ) {
    const {
      isOpen,
      inputRef,
      inputValue,
      setInputValue,
      setIsTyping,
      setQuery,
      onClose,
    } = useSearch()

    const onClear = useCallback(() => {
      setQuery('')
      setInputValue('')
    }, [setInputValue, setQuery])

    const handleClick = useCallback(() => {
      inputValue.length >= 3 ? onClear() : onClose()
    }, [inputValue.length, onClear, onClose])

    const debouncedSetQueryValue = useMemo(() => {
      return _.debounce((value: string) => {
        setIsTyping(false)
        setQuery(value)
      }, 500)
    }, [setQuery, setIsTyping])

    const onValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      setIsTyping(true)
      setInputValue(value)
      debouncedSetQueryValue(value)
    }

    return (
      <FormInput
        ref={mergeRefs(internalRef, inputRef)}
        size='xl'
        id='search'
        name='search'
        type='text'
        autoComplete='off'
        placeholder='Search your game'
        value={inputValue}
        onChange={onValueChange}
        leftIcon={<SearchIcon size='md' />}
        rightIcon={
          <IconButton
            variant='unstyled'
            size='2xs'
            color='#CEBFD9'
            display={isOpen ? 'flex' : 'none'}
            _hover={{
              color: 'primary',
            }}
            onClick={handleClick}
          >
            <CloseIcon />
          </IconButton>
        }
        w='full'
        {...inputProps}
      />
    )
  }
)

export default SearchInput
